import { Box, IconButton, Stack, Typography } from '@/components';
import { useAppTexts } from '@/contexts/AppTextsContext';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobal } from '@/contexts/GlobalContext';
import { useProject } from '@/contexts/ProjectContext';
import AppText from '@/entities/AppText';
import AppTranslation from '@/entities/AppTranslation';
import Language from '@/entities/Language';
import classNames from 'classnames';
import { useState } from 'react';
import { LuLanguages, LuTable } from 'react-icons/lu';
import styles from './Editor.module.css';
import TranslationEditor from './TranslationEditor';

export interface EditorProps {
  appTexts: AppText[];
  languages: Language[];
}

const Editor = (props: EditorProps): JSX.Element => {
  const { appUser } = useAuth();
  const { project } = useProject();
  const { googleTranslateAppText } = useAppTexts();
  const { googleTranslateOnlyEmptyTranslations } = useGlobal();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [onEditTranslation, setOnEditTranslation] = useState<
    AppTranslation | undefined
  >();

  const resetOnEdit = () => {
    setOnEditTranslation(undefined);
  };

  const setOnEdit = (appText: AppText, translation: AppTranslation) => {
    setOnEditTranslation(translation);
  };

  return (
    <div className={styles.container}>
      {props.appTexts.map((appText) => {
        const hasMainLanguageTranslation =
          project?.mainLanguage?.code &&
          appText.translations[project.mainLanguage?.code].value;
        return (
          <div id={appText.id} key={appText.reference} className={styles.row}>
            <div className={styles.key}>
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {appText.isOptionSet ? (
                    <>
                      <LuTable width="16px" height="16px" />
                      <Typography>{appText.key}</Typography>
                      <Typography>•</Typography>
                      <Typography>{appText.optionSetValueKey}</Typography>
                    </>
                  ) : (
                    <Typography>{appText.key}</Typography>
                  )}
                </Stack>
              </Box>

              <Box>
                <IconButton
                  icon={<LuLanguages />}
                  disabled={!hasMainLanguageTranslation}
                  onClick={async () => {
                    if (project?.mainLanguage) {
                      setIsLoading(true);
                      resetOnEdit();
                      await googleTranslateAppText(
                        appText,
                        project.mainLanguage,
                        googleTranslateOnlyEmptyTranslations,
                        appUser?.googleApiKey,
                      );
                      setIsLoading(false);
                    }
                  }}
                />
              </Box>
            </div>
            <div className={styles.translations}>
              {props.languages.map((language) => {
                const isMainLanguage =
                  language.code === project?.mainLanguage?.code;
                const translation: AppTranslation =
                  appText.translations[language.code];
                return (
                  <div
                    key={translation.id}
                    id={translation.id}
                    className={classNames(styles.translation, {
                      [styles.mainLanguage]: isMainLanguage,
                    })}
                  >
                    <div className={styles.translationLanguage}>
                      <Typography>{language.name}</Typography>
                    </div>
                    <TranslationEditor
                      appText={appText}
                      translation={translation}
                      isEditing={translation.id === onEditTranslation?.id}
                      onClose={resetOnEdit}
                      onEdit={() => {
                        setOnEdit(appText, translation);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Editor;
